import React, { useCallback, useMemo } from 'react';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import {
  DocumentTemplateType,
  INumberedDocumentView,
  INumberedDocumentViewWithRagFlag,
} from '../../api-client/autogenerated';
import { documentTypeToUrl, openInNewTab } from '../../scripts/utils';
import { Attachment, ChatOutlined, DescriptionOutlined } from '@material-ui/icons';
import { descendingComparator } from '../document-index/DocumentIndexUtils';
import { Tooltip } from '@material-ui/core';

const useStyles = makeStyles(() =>
  createStyles({
    documentItem: {
      padding: 16,
      paddingLeft: 47,
      border: '1px solid transparent',
      cursor: 'pointer',
      position: 'relative',
      '&:hover': {
        backgroundColor: '#F5F5F5',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        left: '24px',
        top: -2,
        bottom: 0,
        width: '2px',
        backgroundColor: '#0947B9',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '24px',
        top: 24,
        width: '12px',
        height: '2px',
        backgroundColor: '#0947B9',
      },
      '&:last-child': {
        '&::before': {
          height: 28,
        },
      },
    },
    fileItem: {
      '&:hover': {
        backgroundColor: '#E0E0E0',
      },
    },
  }),
);

const SINGLE_MATCH_CONTEXT = 50;
const MULTI_MATCH_CONTEXT = 32;

const findAndHighlightMatches = (document: INumberedDocumentView, searchQueries: string[]) => {
  if (!document || !Array.isArray(searchQueries) || searchQueries.length === 0) return null;

  const MATCH_CONTEXT =
    searchQueries.filter((q) => !!q.trim()).length > 1 ? MULTI_MATCH_CONTEXT : SINGLE_MATCH_CONTEXT;

  const fieldsToSearch = [
    document?.title,
    document?.description,
    document?.currentWorkInProgress,
    document?.submittalSectionDescription,
    document?.submittalDivisionDescription,
    ...(document.comments?.map((c) => c.text) || []),
  ];

  // Normalize, dedupe, drop empties
  const queries = Array.from(
    new Set(searchQueries.map((q) => (q ?? '').trim().toLowerCase()).filter((q) => q.length > 0)),
  );
  if (queries.length === 0) return null;

  // Find all [start, end) ranges for a needle in haystack (case-insensitive; pass lowercased values)
  const findAllRanges = (haystack: string, needle: string): Array<[number, number]> => {
    const ranges: Array<[number, number]> = [];
    let from = 0;
    while (true) {
      const idx = haystack.indexOf(needle, from);
      if (idx === -1) break;
      ranges.push([idx, idx + needle.length]);
      from = idx + needle.length; // advance to avoid infinite loop
    }
    return ranges;
  };

  // Merge overlapping/adjacent [start, end) ranges
  const mergeRanges = (ranges: Array<[number, number]>): Array<[number, number]> => {
    if (ranges.length === 0) return ranges;
    const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
    const merged: Array<[number, number]> = [];
    let [curStart, curEnd] = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const [s, e] = sorted[i];
      if (s <= curEnd) {
        curEnd = Math.max(curEnd, e);
      } else {
        merged.push([curStart, curEnd]);
        [curStart, curEnd] = [s, e];
      }
    }
    merged.push([curStart, curEnd]);
    return merged;
  };

  for (let i = 0; i < fieldsToSearch.length; i++) {
    const text = fieldsToSearch[i];
    if (!text) continue;

    if (!text) continue;

    const lower = text.toLowerCase();

    // Exact match ranges across all queries, merged
    const hitRanges = mergeRanges(queries.flatMap((q) => findAllRanges(lower, q)));
    if (hitRanges.length === 0) continue;

    // Expand each hit by MATCH_CONTEXT on both sides, then merge to form display chunks
    const expanded = hitRanges.map(
      ([s, e]) =>
        [Math.max(0, s - MATCH_CONTEXT), Math.min(text.length, e + MATCH_CONTEXT)] as [
          number,
          number,
        ],
    );
    const displayRanges = mergeRanges(expanded);

    const segments: React.ReactNode[] = [];

    // Leading ellipsis if we’re not starting at the beginning
    if (displayRanges[0][0] > 0) segments.push('...');

    displayRanges.forEach(([ds, de], idx) => {
      let cursor = ds;

      // Get the highlight ranges that intersect this display chunk, clipped to [ds, de]
      const highlightsInRange = hitRanges
        .map(([s, e]) => [Math.max(s, ds), Math.min(e, de)] as [number, number])
        .filter(([s, e]) => e > s)
        .sort((a, b) => a[0] - b[0]);

      highlightsInRange.forEach(([hs, he]) => {
        if (cursor < hs) segments.push(text.slice(cursor, hs)); // unhighlighted context
        segments.push(<strong key={`${hs}-${he}`}>{text.slice(hs, he)}</strong>); // match
        cursor = he;
      });

      if (cursor < de) segments.push(text.slice(cursor, de)); // trailing context in this chunk

      // Ellipsis between chunks
      if (idx < displayRanges.length - 1) segments.push('...');
    });

    // Trailing ellipsis if we’re not ending at the end
    if (displayRanges[displayRanges.length - 1][1] < text.length) segments.push('...');

    return {
      component: <Typography>{segments}</Typography>,
      source: i < 5 ? 'document' : 'comment',
    };
  }

  return null;
};

interface Props {
  documentType: DocumentTemplateType;
  document: INumberedDocumentViewWithRagFlag & { userFriendlyTitle: string };
  searchTerm: string;
  keywordSearchTerms: string[];
}

export default function DocumentSearchItem(props: Props) {
  const { documentType, document, searchTerm, keywordSearchTerms } = props;
  const classes = useStyles();

  const [fileTooltipOpen, setFileTooltipOpen] = React.useState(false);

  const matches = useMemo(
    () => findAndHighlightMatches(document, [searchTerm, ...keywordSearchTerms]),
    [document, searchTerm, keywordSearchTerms],
  );

  const handleSelectDocument = useCallback(() => {
    const params = new URLSearchParams();
    params.set(
      'query',
      keywordSearchTerms.filter(Boolean).length > 0
        ? searchTerm + ` ${keywordSearchTerms.join(' ')}`
        : searchTerm,
    );
    window.open(
      `/main/projects/${document.projectId}/documents/${documentTypeToUrl[documentType]}/${
        document.id
      }?${params.toString()}`,
      '_blank',
      'noopener,noreferrer',
    );
  }, [document, documentType]);

  const handleSelectFile = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, fileId: string) => {
      event.stopPropagation();

      openInNewTab(fileId, false, searchTerm);
    },
    [searchTerm],
  );

  const getFileSource = useCallback(() => {
    if (document.foundByRag) {
      return (
        <div style={{ marginTop: 12 }}>
          {document.foundByRag
            .sort((a, b) => descendingComparator(a, b, 'createdOn'))
            .map(({ fileId, fileName }) => (
              <Tooltip
                arrow
                placement="right"
                title="View File"
                onOpen={() => setFileTooltipOpen(true)}
                onClose={() => setFileTooltipOpen(false)}
              >
                <div
                  key={fileId}
                  onClick={(e) => handleSelectFile(e, fileId)}
                  className={classes.fileItem}
                  style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                >
                  <Attachment htmlColor="#7A797A" style={{ marginRight: 4 }} />
                  <Typography
                    style={{
                      width: '100%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {fileName}
                  </Typography>
                </div>
              </Tooltip>
            ))}
        </div>
      );
    }

    return null;
  }, [document, classes, handleSelectFile]);

  const content = useMemo(
    () => (
      <div key={document.id} onClick={handleSelectDocument} className={classes.documentItem}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          {document.isDraft ? (
            <Chip
              size="small"
              label="Draft"
              color="secondary"
              style={{ height: 20, fontSize: 12, marginRight: 4 }}
            />
          ) : null}
          <Typography style={{ fontSize: '16px', fontWeight: 600 }}>
            {document.userFriendlyTitle}
          </Typography>
        </div>
        {matches ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {matches.source === 'comment' ? (
              <ChatOutlined
                htmlColor="#7A797A"
                style={{ marginRight: 4, marginTop: 3, marginLeft: 2 }}
              />
            ) : (
              <DescriptionOutlined htmlColor="#7A797A" style={{ marginRight: 4 }} />
            )}
            {matches.component}
          </div>
        ) : null}
        {getFileSource()}
      </div>
    ),
    [document, classes, matches, getFileSource, handleSelectDocument],
  );

  if (fileTooltipOpen) return content;

  return (
    <Tooltip arrow title="View Document" placement="right-start">
      {content}
    </Tooltip>
  );
}
